/**
 * TEMP_STREAM_DEBUG_OVERLAY
 *
 * 临时调试工具：研究模式右侧 SSE 推流实时浮窗的事件总线。
 *
 * 数据流：
 *   业务 push ─┬─> IndexedDB（始终持久化，浮窗未打开时也在收）
 *              └─> worker（仅当浮窗启用时存在，浮窗里展示用）
 *
 *   浮窗 setEnabled(true)：创建 worker → 从 IndexedDB bulkLoad 历史
 *   → 把启用过程中并发到来的 push 排队，bulkLoad 完成后顺序灌入 → 之后实时透传。
 *
 *   这样测试同学不必事先打开浮窗，发现问题后再打开仍能看到完整「历史 + 实时」。
 *
 * 数据生命周期：
 *   - 只保留「当前页签、当前一轮对话」的推流：transport.start() 调用 beginRound(sessionId)，
 *     把本页签 worker / IDB / count 重置为 0，再开始累计本轮数据；
 *   - IndexedDB 按 tabId 分库，多浏览器页签互不覆盖；push 再按 roundId / sessionId 丢弃非本轮帧；
 *   - 浮窗里看到的是 ——「本页签最近一次提问以来收到的全部 raw / event」；
 *   - 不限制条数：单轮对话内全量入库，仅受浏览器 IDB 配额（通常几百 MB～几 GB）自然封顶。
 *
 * 规范豁免说明：
 *   本文件及配套 StreamDebugOverlay.vue / searchWorker.js / storage.js 为
 *   「面向开发者/测试同学」的临时调试工具，不接入业务的 design tokens 与 i18n 体系。
 *   规范例外清单已在 docs/code-standard/design-tokens-usage.md 中允许「场景特殊须注释说明」。
 *
 * 清理方式：
 *   1. 删除整个 src/utils/tempStreamDebug/ 目录
 *   2. git grep -n "TEMP_STREAM_DEBUG_OVERLAY"  按结果删除引用点（App.vue + useResearchTransport.js）
 *   3. 用户浏览器中的 IndexedDB `temp-stream-debug:<tabId>` 库会随浏览器数据清理自然消失，不强求主动删除
 */

import { tempStreamStorage } from './storage';

const DEFAULT_QUERY_LIMIT = 1000;

const state = {
  overlayActive: false,
  rawCount: 0,
  eventCount: 0,
  // 本页签当前一轮对话：start() 自增 roundId；sessionId 可能在首帧后才从 pending 升级
  roundId: 0,
  activeSessionId: '',
  // bulkLoad 进行中标记，期间业务 push 不直接发给 worker，先排队，
  // 避免 worker 收到 [push, bulkLoad...] 这种乱序导致 id 倒挂
  isLoadingHistory: false
};

/**
 * 只收下「本页签当前一轮」的推流：roundId 对不上直接丢；已绑定 sessionId 后，其它会话的帧也丢。
 * raw chunk 往往没有 sessionId，所以空 sessionId 仍视为本轮（靠 roundId 隔离）。
 * @param {{ roundId?: number, sessionId?: string }} [meta]
 * @returns {boolean}
 */
function shouldAcceptPush(meta) {
  const roundId = meta?.roundId;
  if (typeof roundId === 'number' && roundId !== state.roundId) return false;
  const incoming = String(meta?.sessionId || '');
  const active = state.activeSessionId;
  if (active && incoming && incoming !== active) return false;
  return true;
}

async function resetStore() {
  state.rawCount = 0;
  state.eventCount = 0;
  if (worker) worker.postMessage({ type: 'clear' });
  try {
    await tempStreamStorage.clearAll();
  } catch {
    // ignore
  }
  notifySubs();
}

let pushQueue = [];
const subs = new Set();
const pendingQueries = new Map();
let worker = null;
let queryId = 0;

// 模块加载时立即从 storage 拉取已有条数，让接下来的 push id 接续而非从 0 起
// （否则刷新页面后业务 push 写入的 id 会与 storage 既有数据重号）
const countsReady = tempStreamStorage
  .loadCounts()
  .then(({ raw, event }) => {
    if (raw > state.rawCount) state.rawCount = raw;
    if (event > state.eventCount) state.eventCount = event;
  })
  .catch(() => {});

function notifySubs() {
  subs.forEach((cb) => {
    try {
      cb();
    } catch {
      // 调试工具自身故障不影响业务
    }
  });
}

function onWorkerMessage(e) {
  const msg = e.data;
  if (!msg || typeof msg !== 'object') return;

  if (msg.type === 'updated') {
    notifySubs();
    return;
  }

  if (msg.type === 'queryResult') {
    const resolver = pendingQueries.get(msg.requestId);
    if (!resolver) return;
    pendingQueries.delete(msg.requestId);
    resolver(msg.result || { items: [], total: 0 });
  }
}

function createWorker() {
  const w = new Worker(new URL('./searchWorker.js', import.meta.url), { type: 'module' });
  w.addEventListener('message', onWorkerMessage);
  return w;
}

function destroyWorker() {
  if (!worker) return;
  worker.removeEventListener('message', onWorkerMessage);
  worker.terminate();
  worker = null;
  pendingQueries.forEach((resolve) => resolve({ items: [], total: 0 }));
  pendingQueries.clear();
}

async function activateOverlay() {
  if (worker) return;
  worker = createWorker();
  state.isLoadingHistory = true;
  pushQueue = [];

  let history;
  try {
    history = await tempStreamStorage.loadAll();
  } catch {
    history = { raw: [], events: [] };
  }

  // 中途已被 destroyWorker：放弃灌入
  if (!worker) {
    state.isLoadingHistory = false;
    pushQueue = [];
    return;
  }

  worker.postMessage({ type: 'bulkLoad', kind: 'raw', payloads: history.raw });
  worker.postMessage({ type: 'bulkLoad', kind: 'event', payloads: history.events });

  // 用历史中的最大 id 同步主线程 count，让后续 push 接续递增
  const lastRaw = history.raw[history.raw.length - 1];
  const lastEvent = history.events[history.events.length - 1];
  if (lastRaw && typeof lastRaw.id === 'number' && lastRaw.id > state.rawCount) {
    state.rawCount = lastRaw.id;
  }
  if (lastEvent && typeof lastEvent.id === 'number' && lastEvent.id > state.eventCount) {
    state.eventCount = lastEvent.id;
  }

  // bulkLoad 期间累积的实时 push 此刻按顺序发给 worker
  for (let i = 0; i < pushQueue.length; i += 1) worker.postMessage(pushQueue[i]);
  pushQueue = [];
  state.isLoadingHistory = false;

  // 让浮窗拉一次最新数据（包含历史 + 排队期间的实时帧）
  notifySubs();
}

function sendPushToWorker(msg) {
  if (!worker) return;
  if (state.isLoadingHistory) {
    pushQueue.push(msg);
  } else {
    worker.postMessage(msg);
  }
}

export const streamDebugBus = {
  setEnabled(v) {
    const next = !!v;
    if (next === state.overlayActive) return;
    state.overlayActive = next;
    if (next) {
      // 异步加载历史；调用方不需要 await（订阅者会在加载完成时收到 notify）
      activateOverlay();
    } else {
      destroyWorker();
      state.isLoadingHistory = false;
      pushQueue = [];
      notifySubs();
    }
  },
  isEnabled() {
    return state.overlayActive;
  },
  getCount() {
    return { rawCount: state.rawCount, eventCount: state.eventCount };
  },
  getScope() {
    return {
      tabId: tempStreamStorage.getTabId(),
      sessionId: state.activeSessionId,
      roundId: state.roundId
    };
  },
  /**
   * 本页签开始一轮新对话：重置本页签存储，绑定 sessionId（新会话首发可为空，等 bindSession）。
   * @param {string} [sessionId]
   * @returns {number} 本轮 roundId，调用方随 push meta 带回
   */
  beginRound(sessionId) {
    state.roundId += 1;
    state.activeSessionId = String(sessionId || '');
    resetStore();
    return state.roundId;
  },
  /**
   * 首帧带回真实 sessionId 时升级绑定；不重置数据。
   * @param {string} sessionId
   * @param {number} [roundId] 传入时必须与当前轮次一致，避免后台流首帧抢绑
   */
  bindSession(sessionId, roundId) {
    const id = String(sessionId || '');
    if (!id) return;
    if (typeof roundId === 'number' && roundId !== state.roundId) return;
    if (!state.activeSessionId) {
      state.activeSessionId = id;
      notifySubs();
    }
  },
  pushRaw(chunk, meta = {}) {
    if (!shouldAcceptPush(meta)) return;
    // 业务侧 push 始终生效：先落盘（让浮窗未打开时仍累积历史），再喂 worker（仅当浮窗启用）
    state.rawCount += 1;
    const text = typeof chunk === 'string' ? chunk : String(chunk ?? '');
    const payload = {
      id: state.rawCount,
      ts: Date.now(),
      size: text.length,
      text,
      ...meta,
      tabId: tempStreamStorage.getTabId(),
      sessionId: String(meta.sessionId || state.activeSessionId || ''),
      roundId: typeof meta.roundId === 'number' ? meta.roundId : state.roundId
    };
    tempStreamStorage.appendRaw(payload);
    sendPushToWorker({ type: 'push', kind: 'raw', payload });
  },
  pushEvent(event, meta = {}) {
    if (!shouldAcceptPush(meta)) return;
    state.eventCount += 1;
    const payload = {
      id: state.eventCount,
      ts: Date.now(),
      event,
      ...meta,
      tabId: tempStreamStorage.getTabId(),
      sessionId: String(meta.sessionId || state.activeSessionId || ''),
      roundId: typeof meta.roundId === 'number' ? meta.roundId : state.roundId
    };
    tempStreamStorage.appendEvent(payload);
    sendPushToWorker({ type: 'push', kind: 'event', payload });
  },
  query(kind, keyword = '', limit = DEFAULT_QUERY_LIMIT) {
    if (!worker) {
      return Promise.resolve({ items: [], total: 0 });
    }
    queryId += 1;
    const requestId = queryId;
    return new Promise((resolve) => {
      pendingQueries.set(requestId, resolve);
      worker.postMessage({ type: 'query', requestId, kind, keyword, limit });
    });
  },
  clear: resetStore,
  subscribe(cb) {
    subs.add(cb);
    return () => subs.delete(cb);
  },
  /**
   * 给浮窗的一个轻 hook：在 onMounted 时调用，确保 module 顶部异步的 loadCounts
   * 已经结算完，避免页面打开后 push 一两条仍走 0 起步。Promise 不抛错。
   */
  whenReady() {
    return countsReady;
  }
};
